/**
 * API Module Exports
 */

export {
  configureApi,
  ensureContentApiBaseUrl,
  getApiConfig,
  getH5pRequestHeaders,
  getSharedApiAuthHeaders,
  getSharedApiUploadHeaders,
  isApiConfigured,
  sharedApiClient,
  h5pApiClient,
  type SharedApiConfig,
  type ApiResponse,
  type ApiEnvelope,
  type ApiError,
  type MultipartUploadProgress,
  type PostFormDataOptions,
} from './client';

export {
  normalizeEnvelopeErrorDetails,
  flattenApiErrorDetails,
  fieldPathFromDetailKey,
  type FieldErrorItem,
} from './fieldErrors';

export {
  contentService,
  fetchContent,
  fetchContentByMetaId,
  fetchH5PEditorModel,
  fetchH5PPlayerModel,
  extractH5PContentFromData,
  countSubQuestions,
  // Content Status constants and helpers
  ContentStatus,
  ContentStatusLabels,
  getStatusLabel,
  getStatusValue,
  normalizeContentStatus,
  // Types
  type ContentStatusValue,
  type ContentStatusLabel,
  type Content,
  type ContentTagRef,
  type ContentMetadata,
  type ContentType,
  type ContentSource,
  type ContentListItem,
  type ContentListResponse,
  type ContentListParams,
  type ContentDifficulty,
  CONTENT_DIFFICULTY_VALUES,
  type CreateContentData,
  type UpdateContentData,
  type CreateContentTypeData,
  type ContentTypeListParams,
  type ContentTypeListResponse,
  type ContentTypeResponse,
  type H5PContentData,
  type EssayGradeRequest,
  type EssayGradeResult,
  type SpeakingGradePayload,
  type SpeakingGradeResult,
  buildSpeakingGradeFormData,
  type DraftGradeContentType,
  type GradeJobStart,
  type GradeJobStartResult,
  type GradeJobDispatchOptions,
  type ContentVersionItem,
  type RollbackResponse,
} from './content.service';

// Generate Service (AI Content Generation)
export {
  generateService,
  type PollJobStatusOptions,
} from './generate.service';

export {
  isJobTerminal,
  isJobRunning,
  type JobStatus,
  type StepStatus,
  type JobStep,
  type JobProgress,
  type GenerationPhase,
  type JobDetail,
  type GenerateContentRequest,
  type GenerateContentFromDocumentRequest,
  type GenerateContentResponse,
  type RegenerateContentRequest,
  type BulkContentGenerateItem,
  type BulkContentGenerateRequest,
  type BulkContentGenerateResponse,
  type BulkContentGenerateJobItem,
  type BulkGenerateEstimateResponse,
  type BulkGeneratedContent,
  type BulkGeneratedContentProvenance,
  type BulkGeneratedContentsResponse,
  type BulkDiscardContentsRequest,
  type BulkDiscardContentsResponse,
  type JobSummary,
  type BulkJobDetail,
} from './generate.types';

export {
  ERROR_BULK_JOB_NOT_FOUND,
  ERROR_BULK_JOB_STATUS_UNAVAILABLE,
  isTransientBulkJobPollError,
  shouldAbortBulkJobPollingImmediately,
} from './bulkJobPollErrors';

// Speech-timing Service (interactive-reading TTS + word timing) — EAA-1755
export {
  speechTimingService,
  type SpeechTimingWord,
  type SpeechTimingResult,
  type SpeechTimingVoicesResult,
  type SpeechTimingGenerateParams,
} from './speechTiming.service';

// Speaking-score Service (interactive-reading pronunciation grading) — EAA-1754/1755
export {
  speakingScoreService,
  type SpeakingScoreParams,
  type SpeakingScoreResult,
} from './speakingScore.service';

// Pronunciation-score normalisation shared by interactive-reading and speaking — EAA-1857
export {
  normalizePronunciationScore,
  type PronunciationScore,
  type PronunciationWordScore,
  type PronunciationPhoneScore,
  type WordScoreBucket,
} from './pronunciationScore';

// AI Content Type Service
export {
  aiContentTypeService,
} from './aiContentType.service';

export {
  type AIContentType,
  type AIContentTypeCategory,
  type AIContentTypeListResponse,
  type AIContentTypeListParams,
} from './aiContentType.types';

// Question-type groups (tenant-owned AI type grouping) — EAA-2026
export { questionTypeGroupService } from './questionTypeGroup.service';
export {
  type QuestionTypeGroup,
  type QuestionTypeGroupItem,
  type QuestionTypeGroupCatalog,
  type QuestionTypeGroupListParams,
  type QuestionTypeGroupListResponse,
  type CreateQuestionTypeGroupData,
  type UpdateQuestionTypeGroupData,
} from './questionTypeGroup.types';

// Program Service
export {
  programService,
  type ProgramListItem,
  type ProgramListParams,
  type ProgramListResponse,
  type ProgramScope,
  type ProgramType,
  PROGRAM_TYPE_MANUAL,
  PROGRAM_TYPE_AI_GENERATE,
  type ProgramStatusLabel,
  type ProgramDetail,
  type UserItem,
  type EnrollPayload,
  type ProgramTagItem,
  type ProgramDocumentItem,
  type CreateProgramPayload,
  type UpdateProgramPayload,
  type ProgramDocumentPayload,
  type GenerateProgramRequest,
  type GenerateProgramResponse,
  type SuggestProgramTagsRequest,
  type SuggestProgramTagsResponse,
  type RegenerateProgramRequest,
  type ModuleTreeNode,
  type ModuleLevelNested,
  type ModuleContentItem,
  type SourcePageNumbering,
  type SourcePages,
  type SourceEvidence,
  type SourceReferenceImage,
  type SourceProvenance,
  type ModuleContentPage,
  type CreateModulePayload,
  type UpdateModulePayload,
  type TagForAIGroup,
  type TagForAIEntry,
  type ModuleLevelItem,
  type SyncModuleNode,
  type SyncOutlinePayload,
  type OutlineSyncResult,
  type ContentGroupNode,
  type SyncGroupNode,
  type CreateContentGroupPayload,
  type UpdateContentGroupPayload,
  type TextbookActivityCandidate,
  type TextbookActivity,
  type TextbookLessonStatus,
  type TextbookLessonActivities,
  type TextbookActivitiesRequest,
  type TextbookActivitiesResponse,
  type TextbookExtractRequest,
  type TextbookExtractDocument,
  type TextbookExtractResponse,
} from './program.service';

// Tag Service
export {
  tagService,
  type TagType,
  type TagTypeListItem,
  type TagSelectionMode,
  type CreateTagTypeData,
  type UpdateTagTypeData,
  type TagTypeDemoteMasterResponse,
  type TagTypeListParams,
  type TagTypeListResponse,
  type Tag,
  type CreateTagData,
  type UpdateTagData,
  type TagListParams,
  type TagListResponse,
} from './tag.service';

// Version Service (Publish Versioning)
export {
  versionService,
  type VersionEntityType,
  type VersionListItem,
  type VersionListResponse,
  type VersionDetail,
  type VersionPatchPayload,
  type RestoreResponse,
} from './version.service';

// Helper function to configure API from SharedConfig
import { configureApi as configureApiFn } from './client';

export function configureShared(config: {
  apiBaseUrl: string;
  h5pServerUrl: string;
  /**
   * Base URL of the static H5P bundle (`apps/h5p-core`). When set, H5P player
   * models are read from `<base>/manifests/<Ubername>.json` and playback contacts
   * no H5P server — `h5pServerUrl` stays as the fallback and is what the H5P
   * EDITOR still uses. Apps resolve this from their own env
   * (`VITE_H5P_STATIC_BASE`); this package stays env-agnostic.
   */
  h5pStaticBase?: string;
  getAuthToken?: () => string | null;
  timeout?: number;
  onEmbedApiSuccess?: (payload: {
    method: string;
    endpoint: string;
    status: number;
  }) => void;
  /**
   * Primary language tag for API `Accept-Language` (e.g. `en` or `vi`).
   * Should track the app UI locale, not the browser default list.
   */
  getAcceptLanguage?: () => string;
  /**
   * EAA-1682: called on a 401 to refresh the JWT access token; return the new
   * access token, or null/undefined if the refresh failed. See client.ts for why.
   */
  onUnauthorized?: () => Promise<string | null | undefined>;
  /** EAA-1694: acting ACH tenant for an SSO identity, sent as `X-Tenant-Id`. */
  getActingTenantId?: () => string | null;
}): void {
  configureApiFn({
    baseUrl: config.apiBaseUrl,
    h5pUrl: config.h5pServerUrl,
    h5pStaticBase: config.h5pStaticBase,
    getAuthToken: config.getAuthToken,
    timeout: config.timeout,
    onEmbedApiSuccess: config.onEmbedApiSuccess,
    getAcceptLanguage: config.getAcceptLanguage,
    onUnauthorized: config.onUnauthorized,
    getActingTenantId: config.getActingTenantId,
  });
}
